import { useEffect, useState } from 'react';
import { ActionBar, Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { Button, Input, Switch, Skeleton } from '@vnetwork/ui/primitives';
import { LiveConsoleBreadcrumbs } from '../LiveConsoleBreadcrumbs';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useLmsProvisioningConfig,
  useUpdateLmsProvisioningConfig,
} from '@/api/media/live-provisioning';
import type {
  LmsProvisioningConfig,
  UpdateLmsProvisioningPayload,
} from '@/api/media/live-provisioning-types';
import styles from './ProvisioningPage.module.css';

// ─── Form state ───────────────────────────────────────────────────────────────

type ProvForm = UpdateLmsProvisioningPayload;

const toNumber = (value: unknown): number => {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

function configToForm(c: LmsProvisioningConfig): ProvForm {
  return {
    hls_dash: {
      live_config_id: toNumber(c.hls_dash?.live_config_id),
      hls_fragment: toNumber(c.hls_dash?.hls_fragment),
      hls_window: toNumber(c.hls_dash?.hls_window),
      dash_fragment: toNumber(c.hls_dash?.dash_fragment),
      dash_window: toNumber(c.hls_dash?.dash_window),
      impact_exist_linear: Boolean(c.hls_dash?.impact_exist_linear),
    },
    llcu_fa: {
      id: c.llcu_fa?.id ?? '',
      host: c.llcu_fa?.host ?? '',
      entity_name: c.llcu_fa?.entity_name ?? '',
      upload_path: c.llcu_fa?.upload_path ?? '',
      publish_url: c.llcu_fa?.publish_url ?? '',
    },
  };
}

type FormErrors = Partial<Record<string, string>>;

function validateForm(form: ProvForm): FormErrors {
  const errs: FormErrors = {};
  if (form.hls_dash.hls_fragment < 0) errs.hls_fragment = 'Value must be zero or greater.';
  if (form.hls_dash.hls_window < 0) errs.hls_window = 'Value must be zero or greater.';
  if (form.hls_dash.dash_fragment < 0) errs.dash_fragment = 'Value must be zero or greater.';
  if (form.hls_dash.dash_window < 0) errs.dash_window = 'Value must be zero or greater.';
  if (!form.llcu_fa.host.trim()) errs.host = 'Host is required.';
  if (!form.llcu_fa.entity_name.trim()) errs.entity_name = 'Entity name is required.';
  if (!form.llcu_fa.upload_path.trim()) errs.upload_path = 'Upload path is required.';
  if (!form.llcu_fa.publish_url.trim()) errs.publish_url = 'Publish URL is required.';
  return errs;
}

// ─── Number field with inline unit label ─────────────────────────────────────

interface NumberWithUnitProps {
  value: number;
  unit: string;
  invalid?: boolean;
  onChange: (v: number) => void;
  // Field render props forwarded for label association
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
}

function NumberWithUnit({
  value,
  unit,
  invalid,
  onChange,
  ...fieldProps
}: NumberWithUnitProps) {
  return (
    <Input
      type="number"
      value={value}
      invalid={invalid}
      endSlot={
        <span style={{
          fontSize: 'var(--vn-font-size-xs)',
          color: 'var(--vn-fg-muted)',
          paddingRight: 'var(--vn-space-2)',
          whiteSpace: 'nowrap',
        }}>
          {unit}
        </span>
      }
      onChange={(e) => onChange(toNumber(e.target.value))}
      {...fieldProps}
    />
  );
}

// ─── Main provisioning page ───────────────────────────────────────────────────

export function LiveProvisioning({ serviceId }: { serviceId: string }) {
  const toast = useToast();

  const { data: config, isLoading, error, refetch } = useLmsProvisioningConfig(serviceId);
  const updateMutation = useUpdateLmsProvisioningConfig(serviceId);

  const [form, setForm] = useState<ProvForm | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Sync config → form on first load
  useEffect(() => {
    if (config && !form) {
      setForm(configToForm(config));
      setIsDirty(false);
    }
  }, [config, form]);

  const patch = (updater: (prev: ProvForm) => ProvForm) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = updater(prev);
      setIsDirty(true);
      return next;
    });
  };

  const reset = () => {
    if (config) { setForm(configToForm(config)); setIsDirty(false); setErrors({}); }
  };

  const save = () => {
    if (!form) return;
    const errs = validateForm(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    updateMutation.mutate(form, {
      onSuccess: () => { toast.success('Provisioning config saved'); setIsDirty(false); refetch(); },
      onError: (err) => toast.error((err as Error).message || 'Failed to save provisioning config'),
    });
  };

  if (error) {
    return (
      <Container size="xl">
        <PageHeader
          breadcrumbs={<LiveConsoleBreadcrumbs trail={[{ label: 'Provisioning' }]} />}
          title="Provisioning"
          description="HLS/DASH fragment settings and file access configuration."
        />
        <div className={styles.errorBox}>{(error as Error).message || 'Failed to load provisioning config.'}</div>
      </Container>
    );
  }

  if (isLoading || !form) {
    return (
      <Container size="xl">
        <PageHeader
          breadcrumbs={<LiveConsoleBreadcrumbs trail={[{ label: 'Provisioning' }]} />}
          title="Provisioning"
          description="HLS/DASH fragment settings and file access configuration."
        />
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Skeleton variant="rect" height={160} />
          <Skeleton variant="rect" height={160} />
        </div>
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<LiveConsoleBreadcrumbs trail={[{ label: 'Provisioning' }]} />}
        title="Provisioning"
        description="HLS/DASH fragment settings and file access configuration."
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* HLS / DASH */}
      <Section title="HLS / DASH" description="Fragment and window duration for adaptive bitrate outputs." style={{ marginBottom: 0 }}>
        <div className={styles.grid2}>
          <Field label="HLS Fragment" required error={errors.hls_fragment}>
            {(fp) => (
              <NumberWithUnit
                {...fp}
                value={form.hls_dash.hls_fragment}
                unit="sec"
                invalid={!!errors.hls_fragment}
                onChange={(v) => patch((p) => ({ ...p, hls_dash: { ...p.hls_dash, hls_fragment: v } }))}
              />
            )}
          </Field>
          <Field label="HLS Window" required error={errors.hls_window}>
            {(fp) => (
              <NumberWithUnit
                {...fp}
                value={form.hls_dash.hls_window}
                unit="sec"
                invalid={!!errors.hls_window}
                onChange={(v) => patch((p) => ({ ...p, hls_dash: { ...p.hls_dash, hls_window: v } }))}
              />
            )}
          </Field>
          <Field label="DASH Fragment" required error={errors.dash_fragment}>
            {(fp) => (
              <NumberWithUnit
                {...fp}
                value={form.hls_dash.dash_fragment}
                unit="sec"
                invalid={!!errors.dash_fragment}
                onChange={(v) => patch((p) => ({ ...p, hls_dash: { ...p.hls_dash, dash_fragment: v } }))}
              />
            )}
          </Field>
          <Field label="DASH Window" required error={errors.dash_window}>
            {(fp) => (
              <NumberWithUnit
                {...fp}
                value={form.hls_dash.dash_window}
                unit="sec"
                invalid={!!errors.dash_window}
                onChange={(v) => patch((p) => ({ ...p, hls_dash: { ...p.hls_dash, dash_window: v } }))}
              />
            )}
          </Field>
        </div>

        <div className={styles.switchRow}>
          <Switch
            checked={form.hls_dash.impact_exist_linear}
            onCheckedChange={(checked) =>
              patch((p) => ({ ...p, hls_dash: { ...p.hls_dash, impact_exist_linear: checked } }))
            }
            id="impact-linear"
          />
          <label htmlFor="impact-linear">Apply changes to existing linear jobs</label>
        </div>
      </Section>

      {/* LLCU File Access */}
      <Section title="File Access" description="LLCU file access credentials and path configuration." style={{ marginBottom: 0 }}>
        <div className={styles.grid2}>
          {form.llcu_fa.id && (
            <Field label="ID">
              {(fp) => (
                <Input
                  {...fp}
                  value={form.llcu_fa.id}
                  readOnly
                  disabled
                />
              )}
            </Field>
          )}
          <Field label="Host" required error={errors.host}>
            {(fp) => (
              <Input
                {...fp}
                value={form.llcu_fa.host}
                invalid={!!errors.host}
                onChange={(e) => patch((p) => ({ ...p, llcu_fa: { ...p.llcu_fa, host: e.target.value } }))}
                placeholder="files.example.com"
              />
            )}
          </Field>
          <Field label="Entity Name" required error={errors.entity_name}>
            {(fp) => (
              <Input
                {...fp}
                value={form.llcu_fa.entity_name}
                invalid={!!errors.entity_name}
                onChange={(e) => patch((p) => ({ ...p, llcu_fa: { ...p.llcu_fa, entity_name: e.target.value } }))}
                placeholder="entity-name"
              />
            )}
          </Field>
          <Field label="Upload Path" required error={errors.upload_path}>
            {(fp) => (
              <Input
                {...fp}
                value={form.llcu_fa.upload_path}
                invalid={!!errors.upload_path}
                onChange={(e) => patch((p) => ({ ...p, llcu_fa: { ...p.llcu_fa, upload_path: e.target.value } }))}
                placeholder="/upload"
              />
            )}
          </Field>
          <Field label="Publish URL" required error={errors.publish_url}>
            {(fp) => (
              <Input
                {...fp}
                value={form.llcu_fa.publish_url}
                invalid={!!errors.publish_url}
                onChange={(e) => patch((p) => ({ ...p, llcu_fa: { ...p.llcu_fa, publish_url: e.target.value } }))}
                placeholder="https://cdn.example.com/live"
              />
            )}
          </Field>
        </div>
      </Section>

      <ActionBar
        start={
          isDirty ? (
            <span className={styles.dirtyBanner}>You have unsaved changes.</span>
          ) : undefined
        }
        end={
          <>
            <Button
              variant="secondary"
              disabled={!isDirty || updateMutation.isPending}
              onClick={reset}
            >
              Reset
            </Button>
            <Button
              variant="primary"
              disabled={updateMutation.isPending || !isDirty || Object.keys(errors).length > 0}
              onClick={save}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
            </Button>
          </>
        }
      />
      </div>
    </Container>
  );
}
